/**
 * DepartmentPerformanceMetrics
 * Key metrics display for department performance
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Grid,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  Users,
  Zap,
  AlertTriangle,
  Activity,
} from 'lucide-react';

import { DepartmentPerformance } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

interface DepartmentPerformanceMetricsProps {
  performance: DepartmentPerformance | undefined;
  isLoading: boolean;
  period: string;
}

const DepartmentPerformanceMetrics: React.FC<DepartmentPerformanceMetricsProps> = ({
  performance,
  isLoading,
  period,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <Card>
              <CardContent>
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={60} height={40} />
                <Skeleton variant="rectangular" height={8} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!performance) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No performance data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const metrics = [
    {
      label: 'Completion Rate',
      value: formatPercent(performance.completionRate),
      subLabel: `${performance.completedTasks}/${performance.totalTasks} tasks`,
      icon: Target,
      color: performance.completionRate >= 80 ? theme.palette.success.main : 
             performance.completionRate >= 60 ? theme.palette.warning.main : theme.palette.error.main,
      progress: performance.completionRate,
    },
    {
      label: 'On-Time Delivery',
      value: formatPercent(performance.onTimeDeliveryRate),
      subLabel: 'of completed tasks',
      icon: Clock,
      color: performance.onTimeDeliveryRate >= 80 ? theme.palette.success.main : 
             performance.onTimeDeliveryRate >= 60 ? theme.palette.warning.main : theme.palette.error.main,
      progress: performance.onTimeDeliveryRate,
    },
    {
      label: 'Hit Rate',
      value: formatPercent(performance.avgHitRate),
      subLabel: 'avg. performance',
      icon: CheckCircle,
      color: performance.avgHitRate >= 80 ? theme.palette.success.main : 
             performance.avgHitRate >= 60 ? theme.palette.warning.main : theme.palette.error.main,
      progress: performance.avgHitRate,
    },
    {
      label: 'Velocity',
      value: `${performance.avgVelocity}x`,
      subLabel: performance.avgVelocity <= 1 ? 'Faster than est.' : 'Slower than est.',
      icon: performance.avgVelocity <= 1 ? TrendingUp : TrendingDown,
      color: performance.avgVelocity <= 1 ? theme.palette.success.main : theme.palette.warning.main,
      progress: Math.min(performance.avgVelocity * 50, 100),
    },
    {
      label: 'Utilization',
      value: formatPercent(performance.avgUtilization),
      subLabel: `${performance.overloadedMembers} overloaded`,
      icon: Users,
      color: performance.avgUtilization >= 70 && performance.avgUtilization <= 100 
        ? theme.palette.success.main 
        : performance.avgUtilization > 100 ? theme.palette.error.main : theme.palette.warning.main,
      progress: Math.min(performance.avgUtilization, 100),
    },
    {
      label: 'At Risk',
      value: performance.projectsAtRisk,
      subLabel: `of ${performance.activeProjects} active`,
      icon: AlertTriangle,
      color: performance.projectsAtRisk === 0 ? theme.palette.success.main : 
             performance.projectsAtRisk <= 2 ? theme.palette.warning.main : theme.palette.error.main,
      progress: performance.activeProjects > 0 
        ? ((performance.activeProjects - performance.projectsAtRisk) / performance.activeProjects) * 100 
        : 100,
    },
  ];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Activity size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Key Performance Metrics
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {period}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Grid item xs={6} sm={4} md={2} key={metric.label}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(metric.color, 0.08),
                    border: '1px solid',
                    borderColor: alpha(metric.color, 0.2),
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Icon size={16} style={{ color: metric.color }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {metric.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ color: metric.color }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {metric.subLabel}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metric.progress}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: alpha(metric.color, 0.2),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: metric.color,
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Task Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Task Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
                <Typography variant="body2">
                  <strong>{performance.notStartedTasks}</strong> Not Started
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                <Typography variant="body2">
                  <strong>{performance.inProgressTasks}</strong> In Progress
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <Typography variant="body2">
                  <strong>{performance.blockedTasks}</strong> Blocked
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                <Typography variant="body2">
                  <strong>{performance.overdueTasks}</strong> Overdue
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Time Metrics */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Time Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Estimated Hours
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {performance.totalEstimatedHours.toFixed(1)}h
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Actual Hours
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {performance.totalActualHours.toFixed(1)}h
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DepartmentPerformanceMetrics;
